// apps/web/src/components/search/SearchResults.tsx
// Tenant-wide search over creators, campaigns, content, brands.

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../lib/apiClient';

interface ResultGroup {
  title: string;
  path: string;
  results: unknown[];
}

export function SearchResults() {
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';
  const [groups, setGroups] = useState<ResultGroup[]>([]);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(async () => {
    if (!q.trim()) {
      setGroups([]);
      return;
    }
    setLoading(true);
    try {
      const [creators, campaigns, content, brands] = await Promise.all([
        apiClient.post('/api/v1/creators/search', { query: q }).catch(() => ({ data: { creators: [] } })),
        apiClient.get('/api/v1/campaigns').catch(() => ({ data: { campaigns: [] } })),
        apiClient.get('/api/v1/content').catch(() => ({ data: { content: [] } })),
        apiClient.get('/api/v1/brands').catch(() => ({ data: { brands: [] } })),
      ]);
      const matches = (item: unknown) => {
        const record = item as Record<string, unknown>;
        return Object.values(record).some((value) => String(value ?? '').toLowerCase().includes(q.toLowerCase()));
      };
      const filter = (items: unknown[] | undefined) => (items ?? []).filter(matches);
      setGroups([
        { title: 'Creators', path: '/app/creators', results: filter((creators.data as { creators?: unknown[] }).creators) },
        { title: 'Campaigns', path: '/app/campaigns', results: filter((campaigns.data as { campaigns?: unknown[] }).campaigns) },
        { title: 'Content', path: '/app/content', results: filter((content.data as { content?: unknown[] }).content) },
        { title: 'Brands', path: '/app/brands', results: filter((brands.data as { brands?: unknown[] }).brands) },
      ]);
    } catch (err) {
      console.error('search failed', err);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { void runSearch(); }, [runSearch]);

  return (
    <div className="search-results">
      <div className="header">
        <h2>Search Results</h2>
        <p className="muted">Query: “{q}”</p>
      </div>
      {loading ? (
        <div className="loading">Searching…</div>
      ) : (
        groups.map((g) => (
          <section key={g.title} className="result-group">
            <h3>
              {g.title} <span className="muted">({g.results.length})</span>
            </h3>
            {g.results.length === 0 ? (
              <p className="empty-state">No matches.</p>
            ) : (
              <div className="manager-grid">
                {g.results.slice(0, 10).map((item) => (
                  <div className="manager-card" key={(item as { id: string }).id}>
                    <p>{(item as { name?: string; title?: string; displayName?: string }).name ??
                      (item as { title?: string }).title ??
                      (item as { displayName?: string }).displayName ??
                      '—'}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))
      )}
    </div>
  );
}